import React, { useState, useMemo } from 'react';
import { 
  useCollection, 
  resolveEnergyColor, 
  isBasicEnergyCard, 
  BASIC_ENERGY_CONFIG 
} from '../context/CollectionContext';
import { useLanguage } from '../context/LanguageContext';
import { Deck, DeckCardItem } from '../types';
import { 
  Layers, Copy, Check, AlertCircle, 
  Swords, Trophy, Plus, Trash2, Sparkles, Pencil, CheckCircle2,
  ChevronDown, ChevronLeft, ChevronRight, Search, X
} from 'lucide-react';
import { soundEffects } from '../services/audio';
import { CardDetailModal } from '../components/CardDetailModal';
import { CreateDeckModal } from '../components/CreateDeckModal';
import { EditDeckModal } from '../components/EditDeckModal';
import { AddCardModal } from '../components/AddCardModal';

export const DecksPage: React.FC = () => {
  const { decks, cards, selectedCard, setSelectedCard, deleteDeck, removeCardFromDeck, isReadOnly } = useCollection();
  const { 
    t, language, getCardName, 
    localizePlaybookTitle, localizePlaybookStep, 
    localizePrizeTradeTip, localizeDeckText, localizeFormat 
  } = useLanguage();
  const [activeDeckId, setActiveDeckId] = useState<string>(decks[0]?.id || 'deck-1');
  const [copied, setCopied] = useState<boolean>(false);
  const [activeGuideTab, setActiveGuideTab] = useState<'opening' | 'midgame' | 'lategame'>('opening');
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [validateModalOpen, setValidateModalOpen] = useState<boolean>(false);
  const [isDeckPickerOpen, setIsDeckPickerOpen] = useState<boolean>(false);
  const [deckSearchQuery, setDeckSearchQuery] = useState<string>('');

  const currentDeck = decks.find(d => d.id === activeDeckId) || decks[0] || {
    id: 'empty',
    name: 'No Decks Available',
    format: 'Standard',
    format_slug: 'standard',
    archetype: 'Empty',
    badge_color: 'from-slate-800 to-slate-950',
    accent_color: '#64748B',
    summary: 'Build your first deck by clicking the button above.',
    win_condition: 'Configure your custom deck strategy.',
    stats: { pokemon: 0, trainers: 0, energies: 0, total: 0 },
    energy_breakdown: { owned: '0', needed: 'None', missing_count: 0 },
    cards: [],
    strategy_guide: {
      opening: { title: '1. Opening Plan', steps: ['Add cards to the deck.'] },
      midgame: { title: '2. Midgame Plan', steps: ['Develop your board state.'] },
      lategame: { title: '3. Endgame Plan', steps: ['Claim Prize cards.'] },
    },
    prize_trade_tip: 'Aim for a solid 60-card synergy.'
  };

  const handleSelectDeck = (deckId: string) => {
    soundEffects.playClick();
    setActiveDeckId(deckId);
    setActiveGuideTab('opening');
  };

  const currentDeckIndex = useMemo(() => {
    const idx = decks.findIndex(d => d.id === currentDeck.id);
    return idx >= 0 ? idx : 0;
  }, [decks, currentDeck.id]);

  const handlePrevDeck = () => {
    if (decks.length <= 1) return;
    const prevIdx = (currentDeckIndex - 1 + decks.length) % decks.length;
    handleSelectDeck(decks[prevIdx].id);
  };

  const handleNextDeck = () => {
    if (decks.length <= 1) return;
    const nextIdx = (currentDeckIndex + 1) % decks.length;
    handleSelectDeck(decks[nextIdx].id);
  };

  const filteredDecks = useMemo(() => {
    if (!deckSearchQuery.trim()) return decks;
    const q = deckSearchQuery.toLowerCase().trim();
    return decks.filter(d => 
      (d.name || '').toLowerCase().includes(q) ||
      (d.format || '').toLowerCase().includes(q) ||
      (d.archetype || '').toLowerCase().includes(q)
    );
  }, [decks, deckSearchQuery]);

  const handleDeleteCurrentDeck = () => {
    if (isReadOnly) return;
    if (confirm(t('decks.confirmDelete', { name: currentDeck.name }))) {
      soundEffects.playClick();
      deleteDeck(currentDeck.id);
      const remaining = decks.filter(d => d.id !== currentDeck.id);
      if (remaining.length > 0) {
        setActiveDeckId(remaining[0].id);
      }
    }
  };

  const handleCopyDecklist = () => {
    soundEffects.playScan();
    const text = [
      `=== ${currentDeck.name} (${currentDeck.format}) ===`,
      `Archetype: ${currentDeck.archetype}`,
      '',
      '## Pokémon (' + currentDeck.stats.pokemon + ')',
      ...currentDeck.cards.filter(c => c.section === 'pokemon').map(c => `${c.count}x ${c.name} (${c.set})`),
      '',
      '## Trainers (' + currentDeck.stats.trainers + ')',
      ...currentDeck.cards.filter(c => c.section === 'trainers').map(c => `${c.count}x ${c.name} (${c.set})`),
      '',
      '## Energies (' + currentDeck.stats.energies + ')',
      ...currentDeck.cards.filter(c => c.section === 'energies').map(c => `${c.count}x ${c.name} (${c.set})`),
      '',
      'Strategy: ' + currentDeck.win_condition
    ].join('\n');

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const findDeckCardMatch = (cardItem: DeckCardItem) => {
    const itemNameLower = cardItem.name.toLowerCase();
    let matched = cards.find(c => 
      c.name_en.toLowerCase() === itemNameLower || 
      c.name_pt.toLowerCase() === itemNameLower
    );
    if (!matched) {
      matched = cards.find(c => 
        c.name_en.toLowerCase().includes(itemNameLower) ||
        c.name_pt.toLowerCase().includes(itemNameLower) ||
        itemNameLower.includes(c.name_en.toLowerCase()) ||
        itemNameLower.includes(c.name_pt.toLowerCase())
      );
    }
    const isEnergy = cardItem.section === 'energies' || isBasicEnergyCard(cardItem.name, cardItem.name, cardItem.set || '', '', '');
    if (!matched && isEnergy) {
      const color = resolveEnergyColor(cardItem.name, cardItem.name, '', '');
      matched = cards.find(c => 
        (c.card_category === 'Energy' || (c.card_category as string) === 'Energia') && 
        (c.color_code === color || resolveEnergyColor(c.name_pt, c.name_en, c.color_code, c.card_number) === color)
      );
    }
    return matched;
  };

  const handleCardClick = (cardItem: DeckCardItem) => {
    soundEffects.playClick();
    const matchedCard = findDeckCardMatch(cardItem);
    if (matchedCard) {
      setSelectedCard(matchedCard);
    } else {
      const isEnergy = cardItem.section === 'energies' || isBasicEnergyCard(cardItem.name, cardItem.name, cardItem.set || '', '', '');
      const isTrainer = cardItem.section === 'trainers';
      const cat = isEnergy ? 'Energy' : isTrainer ? 'Trainer' : 'Pokémon';
      const color = isEnergy ? resolveEnergyColor(cardItem.name, cardItem.name, '', '') : '';
      const fallbackUrl = isEnergy && BASIC_ENERGY_CONFIG[color] ? BASIC_ENERGY_CONFIG[color].cdn : '';

      setSelectedCard({
        id: `deck-${cardItem.name}`,
        name_pt: cardItem.name,
        name_en: cardItem.name,
        set_pt: cardItem.set || 'Básica',
        set_en: cardItem.set || 'Basic',
        set_code: cardItem.set || 'Básica',
        set_name: cardItem.set || 'Deck',
        card_number: '1',
        total_in_set: '100',
        quantity: cardItem.count,
        quality: 'NM',
        language: 'PT',
        rarity_code: 'C',
        rarity_name: 'Comum',
        card_category: cat,
        color_code: color,
        color_name: cat,
        color_slug: isEnergy ? 'energy' : isTrainer ? 'trainer' : 'colorless',
        color_bg: '#232936',
        is_foil: false,
        extras: '',
        comment: '',
        image_url: fallbackUrl,
        decks: [currentDeck.id]
      });
    }
  };

  return (
    <div className="space-y-6 pb-24 animate-in fade-in duration-300">
      {/* Top Deck Management Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-black font-display text-white flex items-center gap-2">
            <Layers className="w-5 h-5 text-yellow-300" />
            <span>{t('decks.title', { count: decks.length })}</span>
          </h2>
          <p className="text-xs text-slate-400 font-mono">
            {t('decks.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={() => { soundEffects.playClick(); setValidateModalOpen(true); }}
            className="flex-1 sm:flex-none bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold px-2.5 sm:px-3.5 py-2 rounded-2xl shadow-lg transition-all active:scale-95 text-[11px] sm:text-xs font-mono flex items-center justify-center space-x-1.5 border border-slate-700 min-w-0"
          >
            <CheckCircle2 className="w-4 h-4 text-yellow-400 shrink-0" />
            <span className="truncate">{t('decks.validateDeck')}</span>
          </button>

          {!isReadOnly && (
            <button
              onClick={() => { soundEffects.playClick(); setCreateModalOpen(true); }}
              className="flex-1 sm:flex-none bg-pokedex-red hover:bg-pokedex-lightred text-white font-bold px-2.5 sm:px-4 py-2 rounded-2xl shadow-lg transition-all active:scale-95 text-[11px] sm:text-xs font-mono flex items-center justify-center space-x-1.5 border border-white/20 min-w-0"
            >
              <Plus className="w-4 h-4 text-yellow-300 shrink-0" />
              <span className="truncate">{t('decks.createDeck')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Deck Switcher Controls (Thumb-friendly, no horizontal dragging) */}
      <div className="sm:hidden bg-pokedex-card/90 rounded-2xl border border-slate-800 p-2 shadow-md">
        <div className="flex items-center gap-2">
          {/* Previous Deck */}
          <button
            onClick={handlePrevDeck}
            disabled={decks.length <= 1}
            aria-label={language === 'pt' ? 'Deck anterior' : 'Previous deck'}
            className="w-10 h-11 rounded-xl bg-pokedex-darker flex items-center justify-center text-slate-300 hover:text-white border border-slate-700/80 active:scale-95 disabled:opacity-30 disabled:pointer-events-none shrink-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Active Deck Selector Trigger */}
          <button
            onClick={() => { soundEffects.playClick(); setIsDeckPickerOpen(true); }}
            className="flex-1 min-w-0 h-11 px-3 rounded-xl bg-gradient-to-r from-slate-900 to-slate-950 border border-slate-700/80 hover:border-yellow-400/60 flex items-center justify-between gap-2 shadow-inner transition-all active:scale-[0.99]"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-5 h-5 rounded-full bg-pokedex-red text-white flex items-center justify-center text-[10px] font-mono font-bold shrink-0">
                {currentDeckIndex + 1}
              </span>
              <div className="text-left min-w-0">
                <div className="text-xs font-bold text-white truncate font-display">
                  {currentDeck.name}
                </div>
                <div className="text-[10px] font-mono text-slate-400 flex items-center gap-1.5 truncate">
                  <span className="text-yellow-400 font-semibold">{currentDeck.format}</span>
                  <span>•</span>
                  <span className="text-emerald-400 font-semibold">{currentDeck.stats.total || 0} {language === 'pt' ? 'cartas' : 'cards'}</span>
                </div>
              </div>
            </div>
            <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
          </button>

          {/* Next Deck */}
          <button
            onClick={handleNextDeck}
            disabled={decks.length <= 1}
            aria-label={language === 'pt' ? 'Próximo deck' : 'Next deck'}
            className="w-10 h-11 rounded-xl bg-pokedex-darker flex items-center justify-center text-slate-300 hover:text-white border border-slate-700/80 active:scale-95 disabled:opacity-30 disabled:pointer-events-none shrink-0"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Desktop Deck Selector Bar with All-Decks Picker Trigger */}
      <div className="hidden sm:flex items-center space-x-2 overflow-x-auto pb-2 no-scrollbar">
        {decks.map((deck, idx) => {
          const isSelected = deck.id === activeDeckId;
          return (
            <button
              key={deck.id}
              onClick={() => handleSelectDeck(deck.id)}
              className={`px-4 py-2.5 rounded-2xl whitespace-nowrap transition-all duration-200 border flex items-center space-x-2 shrink-0 ${
                isSelected
                  ? 'bg-gradient-to-r from-pokedex-red to-red-700 text-white font-bold border-yellow-400/80 shadow-lg scale-105'
                  : 'bg-pokedex-card/90 text-slate-300 border-slate-800 hover:border-slate-700'
              }`}
            >
              <span className="w-5 h-5 rounded-full bg-black/40 flex items-center justify-center text-[10px] font-mono font-bold">
                {idx + 1}
              </span>
              <span className="text-xs">{deck.name}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono ${
                deck.format_slug === 'expanded' ? 'bg-purple-900/60 text-purple-200' : 'bg-emerald-900/60 text-emerald-200'
              }`}>
                {deck.format}
              </span>
            </button>
          );
        })}

        {/* View All Decks Modal Button */}
        <button
          onClick={() => { soundEffects.playClick(); setIsDeckPickerOpen(true); }}
          className="px-3.5 py-2.5 rounded-2xl bg-pokedex-darker/90 text-slate-400 hover:text-yellow-300 border border-slate-800 hover:border-slate-700 flex items-center gap-1.5 text-xs font-mono whitespace-nowrap transition-all shrink-0"
          title={language === 'pt' ? 'Ver todos os decks' : 'View all decks'}
        >
          <Layers className="w-3.5 h-3.5 text-yellow-400" />
          <span>{language === 'pt' ? `Todos (${decks.length})` : `All (${decks.length})`}</span>
          <ChevronDown className="w-3 h-3 ml-0.5" />
        </button>
      </div>

      {/* Main Active Deck Banner / Strategy Hero Card */}
      <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${currentDeck.badge_color} border-2 border-slate-700/80 p-6 shadow-2xl transition-all`}>
        {/* Glow Accent Sphere */}
        <div 
          className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ backgroundColor: currentDeck.accent_color }}
        />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 min-w-0">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
              <span className="bg-pokedex-red text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full font-mono uppercase tracking-wider shrink-0">
                {t('decks.cardsCount', { count: currentDeck.stats.total || 60 })}
              </span>
              <span className="bg-slate-800 text-yellow-300 text-[10px] font-bold px-2.5 py-0.5 rounded-full font-mono shrink-0">
                {localizeFormat(currentDeck.format)}
              </span>
              <span className="bg-slate-800 text-slate-300 text-[10px] px-2.5 py-0.5 rounded-full font-mono max-w-full truncate">
                {currentDeck.archetype}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black font-display text-white break-words">{currentDeck.name}</h2>
            <p className="text-slate-300 text-xs mt-1 max-w-2xl">{localizeDeckText(currentDeck.summary)}</p>
          </div>

          {/* Action Buttons: Copy, Edit & Delete */}
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 w-full sm:w-auto">
            {!isReadOnly && (
              <button
                onClick={() => { soundEffects.playClick(); setEditModalOpen(true); }}
                className="flex-1 sm:flex-none bg-blue-600/90 hover:bg-blue-600 text-white font-bold px-2.5 sm:px-3.5 py-2 rounded-xl border border-blue-400/40 text-[11px] sm:text-xs flex items-center justify-center space-x-1.5 transition-all active:scale-95 shadow-md font-mono min-w-0"
              >
                <Pencil className="w-3.5 h-3.5 text-yellow-300 shrink-0" />
                <span className="truncate">{t('decks.editDeckBtn')}</span>
              </button>
            )}

            <button
              onClick={handleCopyDecklist}
              className="flex-1 sm:flex-none bg-pokedex-darker hover:bg-slate-800 text-slate-100 font-bold px-2.5 sm:px-3.5 py-2 rounded-xl border border-slate-700 text-[11px] sm:text-xs flex items-center justify-center space-x-1.5 transition-all active:scale-95 shadow-md font-mono min-w-0"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-400 shrink-0" /> : <Copy className="w-4 h-4 text-yellow-300 shrink-0" />}
              <span className="truncate">{copied ? t('decks.decklistCopied') : t('decks.exportLive')}</span>
            </button>

            {!isReadOnly && decks.length > 1 && (
              <button
                onClick={handleDeleteCurrentDeck}
                title={t('decks.deleteDeck')}
                className="w-full sm:w-auto p-2 rounded-xl bg-red-950/60 hover:bg-red-900 border border-red-800/60 text-red-300 transition-colors flex items-center justify-center gap-1.5 text-xs font-mono"
              >
                <Trash2 className="w-4 h-4" />
                <span className="sm:hidden">{t('decks.deleteDeck')}</span>
              </button>
            )}
          </div>
        </div>

        {/* Deck Composition Ratio & Energy Requirement */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4 border-t border-slate-800/80 text-xs font-mono">
          {/* Card Ratio */}
          <div className="bg-pokedex-darker p-3 rounded-2xl border border-slate-800 space-y-1.5">
            <span className="text-slate-400 block text-[10px] uppercase">{t('decks.composition', { count: currentDeck.stats.total })}</span>
            <div className="flex items-center justify-between font-bold">
              <span className="text-blue-400">{t('decks.pokemonCount', { count: currentDeck.stats.pokemon })}</span>
              <span className="text-teal-400">{t('decks.trainersCount', { count: currentDeck.stats.trainers })}</span>
              <span className="text-amber-400">{t('decks.energiesCount', { count: currentDeck.stats.energies })}</span>
            </div>
            {/* Visual Bar */}
            <div className="w-full h-2 rounded-full overflow-hidden flex">
              <div style={{ width: `${currentDeck.stats.total ? (currentDeck.stats.pokemon / currentDeck.stats.total) * 100 : 0}%` }} className="bg-blue-500"></div>
              <div style={{ width: `${currentDeck.stats.total ? (currentDeck.stats.trainers / currentDeck.stats.total) * 100 : 0}%` }} className="bg-teal-500"></div>
              <div style={{ width: `${currentDeck.stats.total ? (currentDeck.stats.energies / currentDeck.stats.total) * 100 : 0}%` }} className="bg-amber-500"></div>
            </div>
          </div>

          {/* Energy Status */}
          <div className="bg-pokedex-darker p-3 rounded-2xl border border-slate-800 space-y-1">
            <span className="text-slate-400 block text-[10px] uppercase">{t('decks.energyBreakdown')}</span>
            <div className="text-slate-200 font-semibold text-xs">{localizeDeckText(currentDeck.energy_breakdown.owned)}</div>
            {currentDeck.energy_breakdown.missing_count > 0 ? (
              <div className="flex items-center space-x-1.5 text-amber-300 font-bold text-[11px] pt-1">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>{t('decks.energyRequired', { needed: currentDeck.energy_breakdown.needed })}</span>
              </div>
            ) : (
              <div className="text-emerald-400 font-bold text-[11px] pt-1">{t('decks.energyComplete')}</div>
            )}
          </div>

          {/* Win Condition */}
          <div className="bg-pokedex-darker p-3 rounded-2xl border border-slate-800 space-y-1">
            <span className="text-slate-400 block text-[10px] uppercase flex items-center gap-1">
              <Trophy className="w-3 h-3 text-yellow-400" /> {t('decks.winCondition')}
            </span>
            <p className="text-slate-300 text-[11px] font-sans line-clamp-2">
              {localizeDeckText(currentDeck.win_condition)}
            </p>
          </div>
        </div>
      </div>

      {/* Strategic Playbook Tabs */}
      <div className="bg-pokedex-card/90 rounded-3xl border border-slate-800 p-5 shadow-lg space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-800 pb-3 gap-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Swords className="w-5 h-5 text-pokedex-lightred" />
              <h3 className="font-bold text-sm text-white uppercase tracking-wider font-mono">
                {t('decks.playbook')}
              </h3>
            </div>
            {!isReadOnly && (
              <button
                onClick={() => { soundEffects.playClick(); setEditModalOpen(true); }}
                className="sm:hidden text-[11px] text-yellow-300 hover:text-yellow-200 flex items-center gap-1 bg-black/40 px-2.5 py-1 rounded-lg border border-slate-700 font-mono"
              >
                <Pencil className="w-3 h-3" />
                <span>{t('common.edit')}</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="flex w-full sm:w-auto bg-pokedex-darker p-1 rounded-xl border border-slate-800 text-xs font-mono">
              <button
                onClick={() => { soundEffects.playClick(); setActiveGuideTab('opening'); }}
                className={`flex-1 sm:flex-none px-2.5 sm:px-3 py-1.5 rounded-lg transition-colors text-[11px] sm:text-xs text-center truncate ${
                  activeGuideTab === 'opening' ? 'bg-pokedex-red text-white font-bold' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {t('decks.opening')}
              </button>
              <button
                onClick={() => { soundEffects.playClick(); setActiveGuideTab('midgame'); }}
                className={`flex-1 sm:flex-none px-2.5 sm:px-3 py-1.5 rounded-lg transition-colors text-[11px] sm:text-xs text-center truncate ${
                  activeGuideTab === 'midgame' ? 'bg-pokedex-red text-white font-bold' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {t('decks.midgame')}
              </button>
              <button
                onClick={() => { soundEffects.playClick(); setActiveGuideTab('lategame'); }}
                className={`flex-1 sm:flex-none px-2.5 sm:px-3 py-1.5 rounded-lg transition-colors text-[11px] sm:text-xs text-center truncate ${
                  activeGuideTab === 'lategame' ? 'bg-pokedex-red text-white font-bold' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {t('decks.endgame')}
              </button>
            </div>

            {!isReadOnly && (
              <button
                onClick={() => { soundEffects.playClick(); setEditModalOpen(true); }}
                className="hidden sm:flex text-xs text-yellow-300 hover:text-yellow-200 items-center gap-1.5 bg-black/40 hover:bg-black/60 px-3 py-1.5 rounded-xl border border-slate-700 font-mono transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                <span>{t('decks.customizePlaybook')}</span>
              </button>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-4 bg-pokedex-darker rounded-2xl border border-slate-800 space-y-2.5">
          <h4 className="font-bold text-yellow-300 text-xs font-mono uppercase">
            {localizePlaybookTitle(currentDeck.strategy_guide[activeGuideTab].title)}
          </h4>
          <ul className="space-y-2 text-xs text-slate-300 leading-relaxed font-sans">
            {currentDeck.strategy_guide[activeGuideTab].steps.map((step, sIdx) => (
              <li key={sIdx} className="flex items-start space-x-2">
                <span className="w-1.5 h-1.5 rounded-full bg-pokedex-blue mt-1.5 shrink-0"></span>
                <span>{localizePlaybookStep(step)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Prize Trade Tip Footer */}
        <div className="p-3 bg-purple-950/30 rounded-2xl border border-purple-800/40 text-xs font-mono flex items-start space-x-2.5">
          <Sparkles className="w-4 h-4 text-purple-300 shrink-0 mt-0.5" />
          <div>
            <span className="font-bold text-purple-300">{t('decks.prizeTradeTip')} </span>
            <span className="text-purple-200">{localizePrizeTradeTip(currentDeck.prize_trade_tip)}</span>
          </div>
        </div>
      </div>

      {/* Complete Cards List Grouped */}
      <div className="space-y-4">
        <h3 className="font-bold text-base text-white flex items-center space-x-2">
          <Layers className="w-5 h-5 text-yellow-300" />
          <span>{t('decks.completeList')} ({currentDeck.cards.reduce((acc, c) => acc + c.count, 0)} {t('common.cards')})</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Pokémon Column */}
          <div className="bg-pokedex-card/90 rounded-2xl border border-slate-800 p-4 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2">
              <span className="font-bold text-xs text-blue-400 font-mono uppercase">{t('decks.pokemonSection', { count: currentDeck.stats.pokemon })}</span>
            </div>
            <div className="space-y-1.5">
              {currentDeck.cards.filter(c => c.section === 'pokemon').map((c, i) => {
                const matchedCard = findDeckCardMatch(c);
                const displayName = matchedCard ? getCardName(matchedCard) : c.name;
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between p-2 rounded-xl bg-pokedex-darker hover:bg-slate-800/90 border border-slate-800 transition-colors text-xs"
                  >
                    <div 
                      onClick={() => handleCardClick(c)}
                      className="flex items-center space-x-2 truncate cursor-pointer flex-1"
                    >
                      <span className="font-mono font-bold text-yellow-300">{c.count}x</span>
                      <span className="text-slate-100 font-semibold truncate hover:text-cyan-300">{displayName}</span>
                    </div>
                    {!isReadOnly && (
                      <button
                        onClick={() => removeCardFromDeck(currentDeck.id, c.name)}
                        title={t('decks.removeFromDeck')}
                        className="text-slate-500 hover:text-red-400 p-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Trainers Column */}
          <div className="bg-pokedex-card/90 rounded-2xl border border-slate-800 p-4 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2">
              <span className="font-bold text-xs text-teal-400 font-mono uppercase">{t('decks.trainersSection', { count: currentDeck.stats.trainers })}</span>
            </div>
            <div className="space-y-1.5">
              {currentDeck.cards.filter(c => c.section === 'trainers').map((c, i) => {
                const matchedCard = findDeckCardMatch(c);
                const displayName = matchedCard ? getCardName(matchedCard) : c.name;
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between p-2 rounded-xl bg-pokedex-darker hover:bg-slate-800/90 border border-slate-800 transition-colors text-xs"
                  >
                    <div 
                      onClick={() => handleCardClick(c)}
                      className="flex items-center space-x-2 truncate cursor-pointer flex-1"
                    >
                      <span className="font-mono font-bold text-yellow-300">{c.count}x</span>
                      <span className="text-slate-100 font-semibold truncate hover:text-cyan-300">{displayName}</span>
                    </div>
                    {!isReadOnly && (
                      <button
                        onClick={() => removeCardFromDeck(currentDeck.id, c.name)}
                        title={t('decks.removeFromDeck')}
                        className="text-slate-500 hover:text-red-400 p-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Energies Column */}
          <div className="bg-pokedex-card/90 rounded-2xl border border-slate-800 p-4 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2">
              <span className="font-bold text-xs text-amber-400 font-mono uppercase">{t('decks.energySection', { count: currentDeck.stats.energies })}</span>
            </div>
            <div className="space-y-1.5">
              {currentDeck.cards.filter(c => c.section === 'energies').map((c, i) => {
                const matchedCard = findDeckCardMatch(c);
                const displayName = matchedCard ? getCardName(matchedCard) : c.name;
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between p-2 rounded-xl bg-pokedex-darker hover:bg-slate-800/90 border border-slate-800 transition-colors text-xs"
                  >
                    <div 
                      onClick={() => handleCardClick(c)}
                      className="flex items-center space-x-2 truncate cursor-pointer flex-1"
                    >
                      <span className="font-mono font-bold text-yellow-300">{c.count}x</span>
                      <span className="text-slate-100 font-semibold truncate hover:text-cyan-300">{displayName}</span>
                    </div>
                    {!isReadOnly && (
                      <button
                        onClick={() => removeCardFromDeck(currentDeck.id, c.name)}
                        title={t('decks.removeFromDeck')}
                        className="text-slate-500 hover:text-red-400 p-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Card Detail Modal */}
      <CardDetailModal
        card={selectedCard}
        onClose={() => setSelectedCard(null)}
        onNavigateToDeck={(deckId) => setActiveDeckId(deckId)}
      />

      {/* Create New Deck Modal */}
      {!isReadOnly && (
        <CreateDeckModal
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
        />
      )}

      {/* Edit Deck & Strategic Playbook Modal */}
      {!isReadOnly && (
        <EditDeckModal
          deck={currentDeck}
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
        />
      )}

      {/* Deck Validator Modal */}
      {validateModalOpen && (
        <AddCardModal
          isOpen={validateModalOpen}
          onClose={() => setValidateModalOpen(false)}
          initialTab="validate"
        />
      )}

      {/* Deck Picker Modal (Mobile Bottom-Sheet & Quick Switcher) */}
      {isDeckPickerOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
          <div className="w-full sm:max-w-lg bg-pokedex-card border border-slate-800 rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl flex flex-col max-h-[85vh] animate-in slide-in-from-bottom duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-yellow-400" />
                <h3 className="text-base font-bold font-display text-white">
                  {language === 'pt' ? 'Selecionar Deck' : 'Select Deck'}
                </h3>
                <span className="text-xs font-mono font-bold bg-slate-800 text-slate-300 px-2 py-0.5 rounded-full border border-slate-700">
                  {decks.length}
                </span>
              </div>
              <button
                onClick={() => setIsDeckPickerOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-800/80 flex items-center justify-center text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Quick Search if more than 2 decks */}
            {decks.length > 2 && (
              <div className="pt-3 pb-1">
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={deckSearchQuery}
                    onChange={(e) => setDeckSearchQuery(e.target.value)}
                    placeholder={language === 'pt' ? 'Filtrar deck por nome, formato...' : 'Filter decks by name, format...'}
                    className="w-full bg-pokedex-darker text-white text-xs pl-9 pr-8 py-2.5 rounded-xl border border-slate-800 focus:outline-none focus:border-yellow-400/60 font-sans"
                  />
                  {deckSearchQuery && (
                    <button
                      onClick={() => setDeckSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Vertical List of Decks */}
            <div className="overflow-y-auto py-3 space-y-2 flex-1 no-scrollbar">
              {filteredDecks.map((deck) => {
                const isSelected = deck.id === activeDeckId;
                const originalIndex = decks.findIndex(d => d.id === deck.id);
                return (
                  <button
                    key={deck.id}
                    onClick={() => {
                      handleSelectDeck(deck.id);
                      setIsDeckPickerOpen(false);
                    }}
                    className={`w-full p-3.5 rounded-2xl border text-left transition-all flex items-center justify-between gap-3 ${
                      isSelected
                        ? 'bg-gradient-to-r from-pokedex-red/20 via-pokedex-darker to-pokedex-darker border-yellow-400/80 shadow-md ring-1 ring-yellow-400/30'
                        : 'bg-pokedex-darker/80 hover:bg-pokedex-darker border-slate-800/80 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-mono font-bold shrink-0 ${
                        isSelected ? 'bg-pokedex-red text-white' : 'bg-slate-800 text-slate-400 border border-slate-700'
                      }`}>
                        {originalIndex + 1}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-bold font-display text-white truncate">
                            {deck.name}
                          </h4>
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                            deck.format_slug === 'expanded' ? 'bg-purple-900/60 text-purple-200' : 'bg-emerald-900/60 text-emerald-200'
                          }`}>
                            {deck.format}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 font-sans truncate mt-0.5">
                          {deck.archetype || deck.summary || (language === 'pt' ? 'Deck tático' : 'Tactical deck')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] font-mono font-bold text-slate-300 bg-slate-800/80 px-2 py-1 rounded-lg border border-slate-700">
                        {deck.stats.total || 0}/60
                      </span>
                      {isSelected && (
                        <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })}

              {filteredDecks.length === 0 && (
                <div className="py-8 text-center text-slate-400 text-xs font-mono">
                  {language === 'pt' ? 'Nenhum deck encontrado com esse filtro.' : 'No decks match this filter.'}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {!isReadOnly && (
              <div className="pt-3 border-t border-slate-800 flex items-center justify-between gap-2">
                <button
                  onClick={() => {
                    setIsDeckPickerOpen(false);
                    setCreateModalOpen(true);
                  }}
                  className="w-full py-2.5 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-yellow-300 font-bold text-xs font-mono flex items-center justify-center gap-2 transition-all border border-slate-700"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('decks.createDeck')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
